import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Smartphone,
  MessageCircle,
  CreditCard,
  Bell,
  Package,
  Boxes,
  Users,
  ShoppingCart,
  Sparkles,
  Mail,
  Tag,
  BarChart3,
  Zap,
  AlertTriangle,
  MessageSquare,
  Store,
  TrendingUp,
  Eye,
  MousePointerClick,
} from 'lucide-react';
import { useSecureAuth } from '../contexts/SecureAuthContext';
import { useToast } from './ui/Toast';
import axios from '../axiosConfig';
import './PostLoginActionsModal.css';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.revstar.merchant';
const REVSTAR_AI_WHATSAPP_LINK =
  'https://wa.me/2349160477038?text=Hi%20Revstar%20AI%2C%20help%20me%20manage%20my%20account.';
const REVSTAR_AI_QR_SRC = '/revstar-ai-whatsapp-qr.png';
const MOBILE_INSTALLED_KEY = 'revstarMobileAppInstalled';
const MARKETPLACE_DISMISSED_KEY = 'revstarMarketplaceSlideDismissed';
const SESSION_SEEN_KEY_PREFIX = 'revstarLoginActionsSeen_';

const isInsideMobileWebView = () => {
  if (typeof navigator === 'undefined') return false;
  return /RevstarMobile/i.test(navigator.userAgent || '');
};

const WhatsAppGlyph = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const PaystackGlyph = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="3" y="3" width="18" height="3" rx="1" />
    <rect x="3" y="8" width="18" height="3" rx="1" />
    <rect x="3" y="13" width="13" height="3" rx="1" />
    <rect x="3" y="18" width="8" height="3" rx="1" />
  </svg>
);

const Chip = ({ icon: Icon, label }) => (
  <div className="plm-chip">
    <Icon size={18} strokeWidth={2} />
    <span>{label}</span>
  </div>
);

const PostLoginActionsModal = () => {
  const { user, isAuthenticated } = useSecureAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const store = useSelector((s) => s.store?.myStore);

  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [paymentActive, setPaymentActive] = useState(null);
  const [chatbotEnabled, setChatbotEnabled] = useState(null);
  const [mobileInstalled, setMobileInstalled] = useState(() => {
    try {
      return localStorage.getItem(MOBILE_INSTALLED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [marketplaceDismissed, setMarketplaceDismissed] = useState(() => {
    try {
      return localStorage.getItem(MARKETPLACE_DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [notifPermission, setNotifPermission] = useState(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission;
  });

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined;
    if (isInsideMobileWebView()) return undefined;

    const seenKey = `${SESSION_SEEN_KEY_PREFIX}${user.id}`;
    let alreadySeen = false;
    try {
      alreadySeen = sessionStorage.getItem(seenKey) === 'true';
    } catch {
      alreadySeen = false;
    }
    if (alreadySeen) return undefined;

    let cancelled = false;
    Promise.allSettled([
      axios.get('/merchant/payment-gateways/gateways'),
      axios.get('/merchant/chatbot/settings'),
    ]).then(([gatewaysRes, chatbotRes]) => {
      if (cancelled) return;
      if (gatewaysRes.status === 'fulfilled') {
        const gateways = Array.isArray(gatewaysRes.value?.data?.data)
          ? gatewaysRes.value.data.data
          : [];
        setPaymentActive(gateways.some((g) => g.enabled === true));
      } else {
        setPaymentActive(false);
      }
      if (chatbotRes.status === 'fulfilled') {
        const isEnabled =
          chatbotRes.value?.data?.settings?.isEnabled ??
          chatbotRes.value?.data?.isEnabled ??
          false;
        setChatbotEnabled(Boolean(isEnabled));
      } else {
        setChatbotEnabled(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]);

  const slides = useMemo(() => {
    const all = [
      {
        key: 'revstar-ai',
        accent: 'plm-accent-teal',
        icon: WhatsAppGlyph,
        title: 'Revstar AI',
        subtitle: 'Manage your account on WhatsApp with Revstar AI.',
        qr: {
          src: REVSTAR_AI_QR_SRC,
          alt: 'Scan to message Revstar AI on WhatsApp',
        },
        chips: [
          { icon: ShoppingCart, label: 'Orders' },
          { icon: Package, label: 'Products' },
          { icon: BarChart3, label: 'Reports' },
          { icon: Sparkles, label: 'AI help' },
        ],
        primary: {
          label: 'Message Revstar AI',
          icon: WhatsAppGlyph,
          onClick: () => {
            window.open(REVSTAR_AI_WHATSAPP_LINK, '_blank', 'noopener,noreferrer');
          },
        },
        shouldShow: true,
      },
      {
        key: 'marketplace',
        accent: 'plm-accent-teal',
        icon: Store,
        title: 'Marketplace is live',
        subtitle:
          'Boost your products on the Revstar Marketplace and send new shoppers directly to your storefront.',
        chips: [
          { icon: Sparkles, label: 'Priority placement' },
          { icon: Eye, label: 'More impressions' },
          { icon: MousePointerClick, label: 'Direct clicks' },
          { icon: TrendingUp, label: 'Traffic to your store' },
        ],
        primary: {
          label: 'Boost a product',
          onClick: () => {
            try {
              localStorage.setItem(MARKETPLACE_DISMISSED_KEY, 'true');
            } catch {}
            setMarketplaceDismissed(true);
            close();
            navigate('/merchant-marketplace/boosts');
          },
        },
        secondary: {
          label: 'Maybe later',
          onClick: () => {
            try {
              localStorage.setItem(MARKETPLACE_DISMISSED_KEY, 'true');
            } catch {}
            setMarketplaceDismissed(true);
            advance();
          },
        },
        shouldShow: !marketplaceDismissed,
      },
      {
        key: 'mobile',
        accent: 'plm-accent-teal',
        icon: Smartphone,
        title: 'Get the Revstar mobile app',
        subtitle:
          'Manage orders, inventory and POS from your phone — wherever you are.',
        chips: [
          { icon: ShoppingCart, label: 'Orders' },
          { icon: Boxes, label: 'Inventory' },
          { icon: Users, label: 'Customers' },
          { icon: Zap, label: 'POS on the go' },
        ],
        primary: {
          label: 'Get the app',
          onClick: () => {
            window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
          },
        },
        secondary: {
          label: "I've installed it",
          onClick: () => {
            try {
              localStorage.setItem(MOBILE_INSTALLED_KEY, 'true');
            } catch {}
            setMobileInstalled(true);
            toast.success('Nice — we won’t nudge you again');
            advance();
          },
        },
        shouldShow: !mobileInstalled,
      },
      {
        key: 'livechat',
        accent: 'plm-accent-violet',
        icon: MessageCircle,
        title: 'Activate Livechat on your storefront',
        subtitle:
          'Talk to visitors before they leave — AI replies on your behalf when you’re away.',
        chips: [
          { icon: Sparkles, label: 'AI replies' },
          { icon: Mail, label: 'Lead capture' },
          { icon: Bell, label: 'Email alerts' },
          { icon: BarChart3, label: 'Sales attribution' },
        ],
        primary: {
          label: 'Activate Livechat',
          onClick: () => {
            close();
            navigate('/storefront-chat/settings');
          },
        },
        shouldShow: chatbotEnabled === false,
      },
      {
        key: 'payments',
        accent: 'plm-accent-blue',
        icon: CreditCard,
        title: 'Connect a payment gateway',
        subtitle:
          'Take card and transfer payments — funds land in your bank in minutes.',
        chips: [
          { icon: PaystackGlyph, label: 'Paystack' },
          { icon: PaystackGlyph, label: 'Flutterwave' },
          { icon: CreditCard, label: 'Stripe' },
          { icon: Tag, label: 'Bank Transfer' },
        ],
        primary: {
          label: 'Connect a gateway',
          onClick: () => {
            close();
            navigate('/settings?tab=payments');
          },
        },
        shouldShow: paymentActive === false,
      },
      {
        key: 'whatsapp',
        accent: 'plm-accent-green',
        icon: WhatsAppGlyph,
        title: 'Activate WhatsApp Orders',
        subtitle:
          'Sell where your customers already chat — catalog and orders, all in WhatsApp.',
        chips: [
          { icon: Package, label: 'Catalog sync' },
          { icon: ShoppingCart, label: 'Order capture' },
          { icon: Bell, label: 'Reminders' },
          { icon: Mail, label: 'Receipts' },
        ],
        primary: {
          label: 'Activate WhatsApp',
          onClick: () => {
            close();
            navigate('/settings?tab=integrations&open=whatsapp');
          },
        },
        shouldShow: !store?.whatsappOrdersEnabled,
      },
      {
        key: 'browser-notif',
        accent: 'plm-accent-amber',
        icon: Bell,
        title: 'Never miss what matters',
        subtitle:
          'Get instant alerts for new orders, payments, low stock and customer messages.',
        chips: [
          { icon: ShoppingCart, label: 'New orders' },
          { icon: CreditCard, label: 'Payments' },
          { icon: AlertTriangle, label: 'Low stock' },
          { icon: MessageSquare, label: 'Messages' },
        ],
        primary: {
          label: 'Enable notifications',
          onClick: async () => {
            if (notifPermission === 'unsupported') {
              toast.error('This browser doesn’t support notifications');
              return;
            }
            try {
              const result = await Notification.requestPermission();
              setNotifPermission(result);
              if (result === 'granted') {
                toast.success('Notifications enabled');
                advance();
              } else if (result === 'denied') {
                toast.error('Permission blocked — enable it from your browser settings');
              }
            } catch (err) {
              toast.error('Couldn’t request permission');
            }
          },
        },
        shouldShow: notifPermission !== 'granted' && notifPermission !== 'unsupported',
      },
    ];
    return all.filter((s) => s.shouldShow);
  }, [
    marketplaceDismissed,
    mobileInstalled,
    chatbotEnabled,
    paymentActive,
    store?.whatsappOrdersEnabled,
    notifPermission,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    if (isInsideMobileWebView()) return;
    if (paymentActive === null || chatbotEnabled === null) return;

    const seenKey = `${SESSION_SEEN_KEY_PREFIX}${user.id}`;
    try {
      if (sessionStorage.getItem(seenKey) === 'true') return;
    } catch {}

    if (slides.length === 0) return;
    setActiveIdx(0);
    setOpen(true);
    try {
      sessionStorage.setItem(seenKey, 'true');
    } catch {}
  }, [isAuthenticated, user?.id, paymentActive, chatbotEnabled, slides.length]);

  useEffect(() => {
    if (activeIdx >= slides.length && slides.length > 0) {
      setActiveIdx(slides.length - 1);
    }
  }, [activeIdx, slides.length]);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const advance = useCallback(() => {
    setActiveIdx((idx) => {
      if (idx + 1 >= slides.length) {
        setOpen(false);
        return idx;
      }
      return idx + 1;
    });
  }, [slides.length]);

  const goPrev = () => setActiveIdx((idx) => Math.max(0, idx - 1));
  const goNext = () => setActiveIdx((idx) => Math.min(slides.length - 1, idx + 1));

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open || slides.length === 0) return null;

  const slide = slides[activeIdx] || slides[0];
  const Icon = slide.icon;
  const PrimaryIcon = slide.primary.icon;
  const isLast = activeIdx === slides.length - 1;

  return (
    <div className="plm-backdrop" role="dialog" aria-modal="true" aria-labelledby="plm-title">
      <div className={`plm-card ${slide.accent}`}>
        <button
          type="button"
          className="plm-close"
          onClick={close}
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="plm-icon-wrap" aria-hidden="true">
          <div className="plm-icon-ring">
            <div className="plm-icon-inner">
              <Icon size={24} strokeWidth={2} />
            </div>
          </div>
        </div>

        <h2 id="plm-title" className="plm-title">{slide.title}</h2>
        <p className="plm-subtitle">{slide.subtitle}</p>

        {slide.qr ? (
          <div className="plm-qr-wrap">
            <img className="plm-qr" src={slide.qr.src} alt={slide.qr.alt} />
          </div>
        ) : null}

        <div className="plm-chips">
          {slide.chips.map((c) => (
            <Chip key={c.label} icon={c.icon} label={c.label} />
          ))}
        </div>

        <button
          type="button"
          className="plm-primary"
          onClick={slide.primary.onClick}
        >
          {PrimaryIcon ? <PrimaryIcon size={16} /> : null}
          {slide.primary.label}
        </button>

        {slide.secondary ? (
          <button
            type="button"
            className="plm-secondary"
            onClick={slide.secondary.onClick}
          >
            {slide.secondary.label}
          </button>
        ) : (
          <button
            type="button"
            className="plm-secondary"
            onClick={isLast ? close : advance}
          >
            {isLast ? 'Maybe later' : 'Skip for now'}
          </button>
        )}

        {slides.length > 1 ? (
          <div className="plm-carousel">
            <button
              type="button"
              className="plm-nav"
              onClick={goPrev}
              disabled={activeIdx === 0}
              aria-label="Previous"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="plm-dots" role="tablist">
              {slides.map((s, i) => (
                <button
                  key={s.key}
                  type="button"
                  role="tab"
                  aria-selected={i === activeIdx}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`plm-dot ${i === activeIdx ? 'is-active' : ''}`}
                  onClick={() => setActiveIdx(i)}
                />
              ))}
            </div>
            <button
              type="button"
              className="plm-nav"
              onClick={goNext}
              disabled={isLast}
              aria-label="Next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PostLoginActionsModal;
